/* eslint-disable no-unused-expressions */
import cornerstone from 'cornerstone-core';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { WidgetEnabled } from '../../../../../util/ToolbarUtils';
import CornerstoneViewport from 'react-cornerstone-viewport';
import { redux } from '@ohif/core';
import cornerstoneTools from 'cornerstone-tools';
import { CustomOverlay } from '../CustomOverlay';
import { getEnabledElement, setEnabledElement } from '../../../../../util/state';
import { sync } from '../../sync';
import { startMoviePlayer, stopMoviePlayer } from '../../MoviePlayer';
import {
  changeFullViews,
  selectSeries,
  selectView,
  setLayout2DMode,
  setLayout3DMode,
  setLayout3DModeMobile,
  setLayoutMPRMode,
  setLayoutMPRModeMobile,
} from '../../Layout/LayoutUtils';
import store from '../../../../../store/store';
import { LoadingBar } from './LoadingBar';
import { defaultTools } from './defaultTools';
import { addRequestImages, removeRequestImages } from '../../../../../../loaders/internal/imageIdsManager';
import '../../../../../../src/css/ReactCornerstoneViewport.css';
import { handleDoubleTouch } from '../../../../../util/touchEventUtils';
import CornerstoneToolsUtils from '../../../../../model/CornerstoneToolsUtils';
import {
  setCurrentTool,
  setIs3DModeEnabled,
  setIsMPRModeEnabled,
  updateLayoutElement,
} from '../../../../../actions/LayoutActions';
import { RENDER_TYPE_2D, RENDER_TYPE_3D } from '../../../../../model/factoryRenderers';
import { isChildViewport as isChildViewportWindow } from '../../../../../util/isChildViewport';
import toolNames from '../../../Header/Toolbar/toolNames';
import {
  createActiveViewportContext,
  getCrossWindowWindowId,
  setCrossWindowActiveViewport,
} from '../../crossWindow2dReferenceLines';
import { CSS_PIXELS_PER_MM } from '../screenScale';

export const defaultInit2D = { currentTool: defaultTools[0].name };

export const Image2d = ({ instanceIds, index, button, seriesDetails }) => {
  const { setViewportSpecificData } = redux.actions; // guarda datos del viewport para la sincronización
  const lockedWL = false;
  const dispatch = useDispatch();
  const { mainStudyId, studies } = useSelector((state) => state.patient);
  const { isContinuousScroll, isSelectedPreset, selectedViewIndex, currentTool } = useSelector((state) => state.layout);
  const isChildViewport = isChildViewportWindow();

  const currentStudy = studies.filter((study) => study.StudyInstanceUID === mainStudyId)[0];
  const continuous = useRef(isContinuousScroll);
  const isFixedPreset = useRef(isSelectedPreset);
  continuous.current = isContinuousScroll;
  const scrollDirection = useRef('forward');
  const startFrame = scrollDirection.current === 'forward' ? 0 : instanceIds.length - 1;

  const [instances, setInstances] = useState();

  const [progress, setProgress] = useState({});
  const [isImageLoading, setIsImageLoading] = useState(false);
  const imageProgressById = useRef({});
  const instanceIdsRef = useRef(instanceIds);
  instanceIdsRef.current = instanceIds;
  const instanceIdsKey = instanceIds.join('\0');

  const updateSeriesImageProgress = useCallback((imageId, imageProgress, complete = false) => {
    const currentInstanceIds = instanceIdsRef.current;
    if (!currentInstanceIds.includes(imageId)) return;

    const imageProgressByIdCurrent = {
      ...imageProgressById.current,
      [imageId]: {
        ...imageProgressById.current[imageId],
        ...imageProgress,
        complete: imageProgressById.current[imageId]?.complete || complete,
      },
    };
    imageProgressById.current = imageProgressByIdCurrent;

    const completedInstances = currentInstanceIds.reduce(
      (count, id) => count + (imageProgressByIdCurrent[id]?.complete ? 1 : 0),
      0
    );
    const seriesPercentComplete = completedInstances / Math.max(currentInstanceIds.length, 1) * 100;
    const allImagesComplete = currentInstanceIds.length > 0
      && currentInstanceIds.every((id) => imageProgressByIdCurrent[id]?.complete);
    const hasFinishedInstances = completedInstances > 0;

    setProgress((previousProgress) => ({
      ...previousProgress,
      ...imageProgress,
      seriesPercentComplete: hasFinishedInstances ? seriesPercentComplete : null,
      seriesIsIndeterminate: !hasFinishedInstances,
    }));
    setIsImageLoading(!allImagesComplete);
  }, [instanceIdsKey]);

  const handleImageLoadProgress = useCallback((event) => {
    const progressEvent = event.detail;
    updateSeriesImageProgress(progressEvent?.imageId, progressEvent);
  }, [updateSeriesImageProgress]);

  const handleImageLoaded = useCallback((event) => {
    const image = event.detail?.image;
    updateSeriesImageProgress(image?.imageId, {}, true);
  }, [updateSeriesImageProgress]);

  const handleImageLoadFailed = useCallback((event) => {
    updateSeriesImageProgress(event.detail?.imageId, {}, true);
  }, [updateSeriesImageProgress]);

  useEffect(() => {
    imageProgressById.current = {};
    setProgress({});
    setIsImageLoading(false);

    // Cached images may not emit IMAGE_LOADED again when stack prefetch reaches them.
    instanceIdsRef.current.forEach((imageId) => {
      const imageLoadObject = cornerstone.imageCache.getImageLoadObject(imageId);
      if (imageLoadObject?.promise) {
        imageLoadObject.promise.then(
          () => updateSeriesImageProgress(imageId, {}, true),
          () => updateSeriesImageProgress(imageId, {}, true)
        );
      }
    });
  }, [instanceIdsKey, updateSeriesImageProgress]);

  const checkApplyFixedWLPreset = (selectedViewIndex) => {
    const tool = CornerstoneToolsUtils.getCornerstoneTool('Wwwc');
    if (tool.mode === 'active') {
      dispatch(updateLayoutElement(selectedViewIndex, { isFixedWLPreset: true }));
    }
  };

  // track instances
  useEffect(() => {
    if (!instances || instanceIds[0] !== instances[0]) {
      setInstances(instanceIds);
    }
  }, [instanceIds, instances]);

  // track instances manager
  useEffect(() => {
    if (instances) addRequestImages(instances);

    return () => {
      if (instances) removeRequestImages(instances);
    };
  }, [instances]);

  // track sync
  useEffect(
    () => () => {
      setEnabledElement(index, undefined);
      sync();
    },
    []
  );

  const handleKeyDown = useCallback((event, layout, currentStudy, currentViewIndex) => {
    const { SeriesInstanceUID, renderType } = layout.elements[currentViewIndex];
    const key = event.key;

    if (renderType !== RENDER_TYPE_2D) return;
    if (!(key === 'ArrowRight' || key === 'ArrowLeft')) return;

    const seriesNumber = currentStudy.series.findIndex((element) => element.SeriesInstanceUID === SeriesInstanceUID);
    const nextSeriesIndex = (key) => {
      if (key === 'ArrowRight') {
        return seriesNumber < currentStudy.series.length - 1 ? seriesNumber + 1 : 0;
      }
      if (key === 'ArrowLeft') {
        return seriesNumber > 0 ? seriesNumber - 1 : currentStudy.series.length - 1;
      }
      return seriesNumber;
    };

    const nextSeriesIndexValue = nextSeriesIndex(key);
    const nextSerie = currentStudy.series[nextSeriesIndexValue];
    selectSeries(currentStudy, nextSerie, currentViewIndex);
  }, []);

  useEffect(() => {
    const storeState = store.getState((state) => state.layout);
    const { layout } = storeState;
    const currentViewIndex = layout.selectedViewIndex;

    const eventListener = (event) => handleKeyDown(event, layout, currentStudy, currentViewIndex);

    window.addEventListener('keydown', eventListener);

    return () => {
      window.removeEventListener('keydown', eventListener);
    };
  }, [seriesDetails]);

  useEffect(() => {
    isFixedPreset.current = isSelectedPreset;
  }, [isSelectedPreset]);

  useEffect(() => {
    if (!isChildViewport || !currentTool || !window.cornerstoneTools?.[`${currentTool}Tool`]) return;
    CornerstoneToolsUtils.selectCornerstoneTool(currentTool);
  }, [currentTool, isChildViewport]);

  useEffect(() => {
    const handleActiveViewportCommand = (event) => {
      const { windowId, viewIndex, toolName } = event.detail || {};
      const localWindowId = getCrossWindowWindowId();
      if (viewIndex !== index || (windowId && windowId !== localWindowId)) return;

      if ([toolNames.MODE_2D, toolNames.MODE_MPR, toolNames.MODE_3D].includes(toolName)) {
        const { layout } = store.getState();
        const layoutElement = layout.elements[index];
        if (!layoutElement) return;

        if (toolName === toolNames.MODE_2D) {
          setLayout2DMode(layoutElement);
          dispatch(setCurrentTool(toolNames.WWWC));
          return;
        }
        if (!layoutElement.availableRenderers?.includes(RENDER_TYPE_3D)) return;

        if (toolName === toolNames.MODE_MPR) {
          dispatch(setIsMPRModeEnabled(true));
          dispatch(setIs3DModeEnabled(false));
          isMobile ? setLayoutMPRModeMobile(layoutElement) : setLayoutMPRMode(layoutElement);
          dispatch(setCurrentTool(toolNames.CROSS_REFERENCE));
        } else {
          dispatch(setIs3DModeEnabled(true));
          dispatch(setIsMPRModeEnabled(false));
          isMobile ? setLayout3DModeMobile(layoutElement) : setLayout3DMode(layoutElement);
        }
        return;
      }

      const element = getEnabledElement(index);
      if (!element) return;
      try {
        const layoutElement = store.getState().layout.elements[index];
        const viewport = cornerstone.getViewport(element);
        switch (toolName) {
          case toolNames.MAGNIFY_PLUS: {
            const isMagnifyingGlass = Boolean(layoutElement?.isMagnifyingGlass);
            const magnifyTool = cornerstoneTools.store.state.tools.find(
              (cornerstoneTool) => cornerstoneTool.name === 'MagnifyPlus'
            );
            if (!magnifyTool) return;
            if (isMagnifyingGlass) {
              cornerstoneTools.setToolDisabledForElement(
                element,
                magnifyTool.name,
                { mouseButtonMask: 1 }
              );
              cornerstoneTools.setToolActiveForElement(element, 'Wwwc', { mouseButtonMask: 1 });
            } else {
              cornerstoneTools.setToolActiveForElement(
                element,
                magnifyTool.name,
                { mouseButtonMask: 1 }
              );
            }
            dispatch(updateLayoutElement(index, { isMagnifyingGlass: !isMagnifyingGlass }));
            return;
          }
          case toolNames.CINE: {
            if (layoutElement?.isPlayingCine) {
              stopMoviePlayer(index);
            } else {
              startMoviePlayer(index);
            }
            dispatch(updateLayoutElement(index, { isPlayingCine: !layoutElement?.isPlayingCine }));
            return;
          }
          case toolNames.INVERT:
            viewport.invert = !viewport.invert;
            cornerstone.setViewport(element, viewport);
            return;
          case toolNames.WL_PRESET: {
            const { windowCenter, windowWidth } = event.detail.commandOptions || {};
            if (!Number.isFinite(windowCenter) || !Number.isFinite(windowWidth)) return;
            viewport.voi.windowCenter = windowCenter;
            viewport.voi.windowWidth = windowWidth;
            cornerstone.setViewport(element, viewport);
            dispatch(updateLayoutElement(index, { isFixedWLPreset: true }));
            return;
          }
          case toolNames.ROTATE90:
            viewport.rotation += 90;
            break;
          case toolNames.ROTATE270:
            viewport.rotation -= 90;
            break;
          case toolNames.HFLIP:
            viewport.hflip = !viewport.hflip;
            break;
          case toolNames.VFLIP:
            viewport.vflip = !viewport.vflip;
            break;
          case toolNames.RESET_POSITION: {
            const currentVOI = { ...viewport.voi };
            cornerstone.reset(element);
            const resetViewport = cornerstone.getViewport(element);
            resetViewport.voi = currentVOI;
            cornerstone.setViewport(element, resetViewport);
            sync();
            return;
          }
          case toolNames.ZOOM_1_TO_1:
            viewport.scale = 1;
            break;
          case toolNames.ZOOM_FIT:
            cornerstone.fitToWindow(element);
            sync();
            return;
          case toolNames.ZOOM_ACTUAL_SIZE: {
            const image = cornerstone.getEnabledElement(element).image;
            const imagePlane = cornerstone.metaData.get('imagePlaneModule', image?.imageId);
            const pixelSpacing = Number(image?.columnPixelSpacing
              ?? imagePlane?.columnPixelSpacing
              ?? imagePlane?.pixelSpacing?.[1]);
            if (!Number.isFinite(pixelSpacing) || pixelSpacing <= 0) return;
            viewport.scale = pixelSpacing * CSS_PIXELS_PER_MM;
            break;
          }
          default:
            return;
        }
        cornerstone.setViewport(element, viewport);
      } catch (error) {
        // Ignore commands for viewports that are being removed.
      }
    };

    window.addEventListener('viewer-active-viewport-command', handleActiveViewportCommand);
    return () => window.removeEventListener('viewer-active-viewport-command', handleActiveViewportCommand);
  }, [index]);

  const updateWindowLevel = (viewport, image) => {
    viewport.voi.windowWidth = image.windowWidth;
    viewport.voi.windowCenter = image.windowCenter;
  };

  const adjustDisplayedArea = (viewport, image) => {
    if (viewport.displayedArea) {
      viewport.displayedArea.brhc.x = image.columns;
      viewport.displayedArea.brhc.y = image.rows;
    }
  };

  return (
    <>
      {button}
      {isImageLoading && WidgetEnabled('SERIES_LOADING') && <LoadingBar progress={progress} />}
      <CornerstoneViewport
        tools={defaultTools}
        activeTool={defaultInit2D.currentTool}
        viewportOverlayComponent={CustomOverlay}
        imageIds={instanceIds}
        imageIdIndex={startFrame}
        isStackPrefetchEnabled
        loadIndicatorDelay={1000}
        eventListeners={[
          {
            target: 'cornerstone',
            eventName: cornerstone.EVENTS.IMAGE_LOAD_PROGRESS,
            handler: handleImageLoadProgress,
          },
          {
            target: 'cornerstone',
            eventName: cornerstone.EVENTS.IMAGE_LOADED,
            handler: handleImageLoaded,
          },
          {
            target: 'cornerstone',
            eventName: cornerstone.EVENTS.IMAGE_LOAD_FAILED,
            handler: handleImageLoadFailed,
          },
        ]}
        onElementEnabled={(elementEnabledEvt) => {
          const cornerstoneElement = elementEnabledEvt.detail.element;
          setEnabledElement(index, cornerstoneElement);
          if (isChildViewport) {
            const layout = store.getState().layout;
            setCrossWindowActiveViewport(index, createActiveViewportContext(layout, index));
          }
          const activeTool = store.getState().layout.currentTool;
          if (isChildViewport && window.cornerstoneTools?.[`${activeTool}Tool`]) {
            CornerstoneToolsUtils.selectCornerstoneTool(activeTool);
          }
          sync();
          dispatch(
            setViewportSpecificData(index, {
              // TODO: Hack to make sure our plugin info is available from the outset
              plugin: 'cornerstone',
            })
          );

          cornerstoneElement.addEventListener('cornerstonenewimage', (event) => {
            const layout = store.getState().layout;
            const currentLayoutElement = layout.elements[selectedViewIndex];
            const image = event.detail.image;
            updateSeriesImageProgress(image.imageId, {}, true);

            if (seriesDetails && seriesDetails.SeriesModality) {
              const modality = seriesDetails.SeriesModality;
              const viewport = event.detail.viewport;

              if (!currentLayoutElement.isFixedWLPreset) {
                updateWindowLevel(viewport, image);
              }

              if (modality.includes('DX') || modality.includes('MR')) {
                adjustDisplayedArea(viewport, image);
              }
            }
          });

          // quitamos touch rotation porque no funciona bien ni es comodo de utilizar, habria que arreglarlo
          cornerstoneElement.addEventListener(
            'cornerstonetoolstouchrotate',
            (event) => {
              /// /////// Descomentar para usar un pequeño arreglo del rotation (tambien inicializar firstCall=true fuera)
              // const eventData = event.detail;
              // const { element, viewport, rotation } = eventData;
              // if (firstCall === true) {
              //   viewport.rotation += 180;
              //   firstCall = false;
              // }
              // viewport.rotation += rotation;
              // cornerstone.setViewport(element, viewport);
              /// ///////

              event.stopImmediatePropagation();
              event.preventDefault();
            },
            true
          );

          cornerstoneElement.addEventListener(
            cornerstoneTools.EVENTS.TOUCH_START,
            (event) => {
              selectView(index);
              handleDoubleTouch(event.detail.event, () => changeFullViews());
            },
            true
          );

          cornerstoneElement.addEventListener(
            cornerstoneTools.EVENTS.MOUSE_CLICK,
            () => {
              const slider = cornerstoneElement.parentElement.querySelector('.imageSlider');
              slider.focus();
            },
            true
          );

          cornerstoneElement.addEventListener(
            cornerstoneTools.EVENTS.MOUSE_DRAG,
            () => {
              checkApplyFixedWLPreset(selectedViewIndex);
            },
            true
          );

          cornerstoneElement.addEventListener(
            cornerstoneTools.EVENTS.TOUCH_DRAG,
            () => {
              checkApplyFixedWLPreset(selectedViewIndex);
            },
            true
          );

          cornerstoneElement.addEventListener('wheel', (event) => {
            if (continuous.current) {
              const state = store.getState((state) => state.layout);
              const { layout } = state;
              const currentViewIndex = layout.selectedViewIndex;
              const seriesInstanceUID = layout.elements[currentViewIndex].SeriesInstanceUID;
              const seriesNumber = currentStudy.series.findIndex((element) => element.SeriesInstanceUID === seriesInstanceUID);
              const slider = cornerstoneElement.parentElement.querySelector('.imageSlider');

              scrollDirection.current = Math.sign(event.deltaY) === 1 ? 'forward' : 'backwards';

              // UP
              if (scrollDirection.current === 'backwards' && parseInt(slider.value) <= parseInt(slider.min) && event.deltaY < 0) {
                const seriesIndex = seriesNumber > 0 ? parseInt(seriesNumber) - 1 : currentStudy.series.length - 1;
                const nextSerie = currentStudy.series[seriesIndex];

                selectSeries(currentStudy, nextSerie, currentViewIndex);
                slider.value = slider.max;
              }

              // DOWN
              if (scrollDirection.current === 'forward' && parseInt(slider.value) >= parseInt(slider.max) && event.deltaY > 0) {
                const seriesIndex = seriesNumber < currentStudy.series.length - 1 ? parseInt(seriesNumber) + 1 : 0;
                const nextSerie = currentStudy.series[seriesIndex];

                selectSeries(currentStudy, nextSerie, currentViewIndex);
                slider.value = slider.min;
              }
            }
          });
        }}
      />
    </>
  );
};

export default defaultInit2D;
